<template>
    <div>
        <NavbarOne/>

        <div class="flex items-center gap-4 flex-wrap bg-overlay p-14 sm:p-16 before:bg-title before:bg-opacity-70" :style="{backgroundImage:'url(' + bg + ')'}">
            <div class="text-center w-full">
                <h2 class="text-white text-8 md:text-[40px] font-normal leading-none text-center">FAQs</h2>
                <ul class="flex items-center justify-center gap-[10px] text-base md:text-lg leading-none font-normal text-white mt-3 md:mt-4">
                    <li><router-link to="/">Home</router-link></li>
                    <li>/</li>
                    <li class="text-primary">FAQs</li>
                </ul>
            </div>
        </div>

        <div class="s-py-100">
            <div class="container-fluid max-w-[1720px]">
                <!-- Loading skeleton -->
                <div v-if="loading" class="grid lg:grid-cols-2 gap-12 2xl:gap-20" aria-busy="true">
                    <div v-for="n in 2" :key="n">
                        <div class="h-7 w-56 rounded bg-[#F3F4F5] dark:bg-dark-secondary animate-pulse mb-6"></div>
                        <div v-for="m in 4" :key="m" class="h-[68px] rounded bg-[#F3F4F5] dark:bg-dark-secondary animate-pulse mb-4"></div>
                    </div>
                </div>

                <div v-else-if="error" class="rounded-xl border border-[#E8E9EA] dark:border-bdr-clr-drk p-8 text-center">
                    <p class="text-base sm:text-lg text-paragraph dark:text-white-light">{{ error }}</p>
                    <button type="button" class="btn btn-outline mt-6" data-text="Try again" @click="load"><span>Try again</span></button>
                </div>

                <div v-else-if="!faqGroups.length" class="rounded-xl border border-[#E8E9EA] dark:border-bdr-clr-drk p-8 text-center">
                    <p class="text-base sm:text-lg text-paragraph dark:text-white-light">We are still writing this page. Contact us with any question in the meantime.</p>
                </div>

                <div v-else class="faq-wrapper grid lg:grid-cols-2 gap-12 2xl:gap-20">
                    <div v-for="(group, groupIndex) in faqGroups" :key="group.title" data-aos="fade-up">
                        <h3 class="font-semibold leading-none mb-4 md:mb-6 text-2xl md:text-3xl">{{ group.title }}</h3>

                        <div
                            v-for="(item, itemIndex) in group.items"
                            :key="item.id"
                            class="single-faq mb-4"
                            :class="isOpen(groupIndex, itemIndex) ? 'active' : ''"
                            @click="toggle(groupIndex, itemIndex)"
                        >
                            <div class="faq-head p-3 sm:p-5 bg-[#F3F4F5] dark:bg-dark-secondary duration-300 flex items-center justify-between gap-5">
                                <button
                                    type="button"
                                    class="font-medium text-base sm:text-xl text-left text-title dark:text-white"
                                    :aria-expanded="isOpen(groupIndex, itemIndex)"
                                >
                                    {{ item.question }}
                                </button>
                                <div class="plus">
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                            <div class="faq-body p-4 pb-0 max-w-[733px]" :class="isOpen(groupIndex, itemIndex) ? 'block' : 'hidden'">
                                <p v-for="(paragraph, i) in item.answer" :key="i" class="text-base sm:text-lg" :class="i ? 'mt-2' : ''">
                                    {{ paragraph }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-12 md:mt-16 rounded-xl bg-[#F8F5F0] dark:bg-dark-secondary p-8 md:p-12 text-center">
                    <h4 class="text-xl md:text-2xl font-semibold leading-none">Still have a question?</h4>
                    <p class="mt-3 text-base sm:text-lg text-paragraph dark:text-white-light">
                        Call us on {{ company.phone }}, send a message on WhatsApp, or use the contact form — we answer every enquiry.
                    </p>
                    <div class="mt-6 flex gap-4 justify-center flex-wrap">
                        <router-link to="/contact" class="btn btn-solid" data-text="Contact Us"><span>Contact Us</span></router-link>
                        <router-link to="/request-quotation" class="btn btn-outline" data-text="Request a Quotation"><span>Request a Quotation</span></router-link>
                    </div>
                </div>
            </div>
        </div>

        <FooterOne/>
        <ScrollToTop/>
    </div>
</template>

<script setup>
    import { nextTick, onMounted, ref } from 'vue';
    import NavbarOne from '@/components/navbar/navbar-one.vue';
    import bg from '@/assets/img/shortcode/breadcumb.jpg'
    import Aos from 'aos';
    import FooterOne from '@/components/footer/footer-one.vue';
    import ScrollToTop from '@/components/scroll-to-top.vue';
    import { storefrontRequest } from '@/lib/storefront-api';
    import { company } from '@/data/company';

    // Questions come from the admin (Website & App → FAQs) through the public API,
    // already grouped: [{ title, items: [{ id, question, answer: [paragraphs] }] }].
    const faqGroups = ref([]);
    const loading = ref(true);
    const error = ref('');

    async function load() {
        loading.value = true;
        error.value = '';

        try {
            const response = await storefrontRequest('/faqs');
            faqGroups.value = response.data || [];
            await nextTick();
            Aos.refresh();
        } catch (e) {
            error.value = e.message || 'We could not load the questions right now.';
        } finally {
            loading.value = false;
        }
    }

    onMounted(() => {
        Aos.init();
        load();
    });

    // One open question at a time, identified by group and position.
    const open = ref('');

    const key = (groupIndex, itemIndex) => `${groupIndex}-${itemIndex}`;
    const isOpen = (groupIndex, itemIndex) => open.value === key(groupIndex, itemIndex);
    const toggle = (groupIndex, itemIndex) => {
        open.value = isOpen(groupIndex, itemIndex) ? '' : key(groupIndex, itemIndex);
    };
</script>
